import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { TextInput } from "flowbite-react";
import {
  HiOutlineArrowLeft,
  HiOutlineBuildingOffice2,
  HiOutlineCheckCircle,
  HiOutlineEnvelope,
  HiOutlineIdentification,
  HiOutlineTruck,
  HiOutlineUserGroup,
} from "react-icons/hi2";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  Button,
  CompanyLogoEditor,
  GroupedTabs,
  PageLayout,
  SelectCountry,
  ToggleSwitch,
} from "@components";
import { useWorkspaceData } from "@data";
import { panelSm, textPageTitle } from "@design-system";
import { useCache } from "../../context/CacheContext";
import { useCanManageWorkspace } from "../../hooks/useCanManageWorkspace";
import { Company } from "../../models";
import type { ICompany } from "../../types";

type SaveState = "idle" | "saving" | "saved" | "error";

const cardClass = `${panelSm} p-4 sm:p-6`;

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-medium text-text">{label}</span>
    {children}
  </label>
);

const SectionHeading = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <div className="mb-5">
    <h2 className="text-lg font-semibold text-text">{title}</h2>
    <p className="mt-1 text-sm leading-6 text-muted">{description}</p>
  </div>
);

export default function CompanyDetailPage() {
  const { companyId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(["common", "company"]);
  const { companies, drivers, vehicles, loading } = useWorkspaceData();
  const { updateCacheKey } = useCache();
  const sourceCompany = companies.find((item) => item.id === companyId);
  const [company, setCompany] = useState<Company | undefined>(sourceCompany);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  useEffect(() => {
    if (sourceCompany) setCompany(sourceCompany);
  }, [sourceCompany]);

  useEffect(() => {
    if (saveState !== "saved") return;
    const timeout = window.setTimeout(() => setSaveState("idle"), 2500);
    return () => window.clearTimeout(timeout);
  }, [saveState]);

  const activeTab = searchParams.get("tab") || "overview";
  const driverCount = drivers.filter(
    (item) => item.companyId === companyId,
  ).length;
  const vehicleCount = vehicles.filter(
    (item) => item.companyId === companyId,
  ).length;

  const save = useCallback(
    async (updates: Partial<ICompany>) => {
      if (!company) return;
      setSaveState("saving");
      try {
        const updated = await Company.update(company, updates);
        setCompany(updated);
        updateCacheKey();
        setSaveState("saved");
      } catch (error) {
        console.error("Unable to update company", error);
        setSaveState("error");
        throw error;
      }
    },
    [company, updateCacheKey],
  );

  const tabs = useMemo(
    () =>
      company
        ? [
            {
              id: "overview",
              label: t("company:page.tabs.overview"),
              content: (
                <OverviewForm
                  company={company}
                  isSaving={saveState === "saving"}
                  onSave={save}
                />
              ),
            },
            {
              id: "notifications",
              label: t("company:page.notifications.title"),
              content: (
                <NotificationForm
                  company={company}
                  isSaving={saveState === "saving"}
                  onSave={save}
                />
              ),
            },
            {
              id: "payment",
              label: t("company:page.tabs.payment"),
              content: (
                <PaymentForm
                  company={company}
                  isSaving={saveState === "saving"}
                  onSave={save}
                />
              ),
            },
          ]
        : [],
    [company, save, saveState, t],
  );

  if (loading.companies && !company) {
    return (
      <PageLayout>
        <div className="w-full animate-pulse space-y-4">
          <div className="h-44 rounded-lg bg-surface-muted" />
          <div className="h-96 rounded-lg bg-surface-muted" />
        </div>
      </PageLayout>
    );
  }
  if (!company) return <Navigate to="/workspace/companies" replace />;

  const initials = company.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <PageLayout>
      <div className="w-full space-y-4">
        <Link
          to="/workspace/companies"
          className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-brand"
        >
          <HiOutlineArrowLeft className="h-4 w-4" />
          {t("company:page.back")}
        </Link>

        <section className={cardClass}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-center gap-4 sm:gap-5">
              <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-surface-muted text-xl font-semibold text-brand">
                {company.logoUrl ? (
                  <img
                    className="h-full w-full object-contain p-2"
                    src={company.logoUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  initials || <HiOutlineBuildingOffice2 className="h-8 w-8" />
                )}
              </div>
              <div className="min-w-0">
                <p className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-brand">
                  {t("company:page.transport_company")}
                </p>
                <h1 className={`${textPageTitle} truncate`}>{company.name}</h1>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-secondary">
                  <span className="inline-flex items-center gap-1.5">
                    <HiOutlineIdentification className="h-4 w-4" />
                    {company.organizationNumber ||
                      t("company:page.not_registered")}
                  </span>
                  {company.contactEmail && (
                    <span className="inline-flex items-center gap-1.5">
                      <HiOutlineEnvelope className="h-4 w-4" />
                      {company.contactEmail}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:w-64">
              <Stat
                icon={<HiOutlineUserGroup className="h-4 w-4" />}
                value={driverCount}
                label={t("company:page.drivers")}
              />
              <Stat
                icon={<HiOutlineTruck className="h-4 w-4" />}
                value={vehicleCount}
                label={t("company:page.vehicles")}
              />
            </div>
          </div>
        </section>

        <section className={`${panelSm} overflow-hidden`}>
          <GroupedTabs
            tabs={tabs}
            variant="detail"
            initialTabId={activeTab}
            onTabChange={(tab) => setSearchParams({ tab }, { replace: true })}
            ariaLabel={t("company:page.tabs.label")}
          />
        </section>

        <div aria-live="polite" className="min-h-6 text-sm">
          {saveState === "saved" && (
            <span className="inline-flex items-center gap-1.5 text-success">
              <HiOutlineCheckCircle className="h-5 w-5" />{" "}
              {t("company:page.saved")}
            </span>
          )}
          {saveState === "error" && (
            <span className="text-danger">{t("company:page.save_error")}</span>
          )}
        </div>
      </div>
    </PageLayout>
  );
}

const Stat = ({
  icon,
  value,
  label,
}: {
  icon: ReactNode;
  value: number;
  label: string;
}) => (
  <div className="rounded-lg border border-border bg-surface-muted px-3 py-3">
    <div className="flex items-center gap-2 text-secondary">
      <span className="h-4 w-4">{icon}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
    <p className="mt-1 text-xl font-semibold text-text">{value}</p>
  </div>
);

function OverviewForm({ company, isSaving, onSave }: FormProps) {
  const { t } = useTranslation(["common", "company"]);
  const [logoBusy, setLogoBusy] = useState(false);
  return (
    <form
      className="space-y-6 p-4 sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (isSaving || logoBusy) return;
        const data = new FormData(event.currentTarget);
        void onSave({
          name: String(data.get("name") || "").trim(),
          contactEmail: String(data.get("contactEmail") || "").trim() || null,
          organizationNumber: String(
            data.get("organizationNumber") || "",
          ).trim(),
          primaryAddress: {
            ...(company.primaryAddress || {}),
            addressLine: String(data.get("addressLine") || "").trim(),
            postCode: String(data.get("postCode") || "").trim(),
            city: String(data.get("city") || "").trim(),
            countryCode: String(data.get("countryCode") || "NO"),
          },
        }).catch(() => undefined);
      }}
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className={cardClass}>
          <SectionHeading
            title={t("company:page.general.title")}
            description={t("company:page.general.description")}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t("common:name")}>
              <TextInput
                name="name"
                defaultValue={company.name}
                maxLength={120}
                required
              />
            </Field>
            <Field label={t("common:organization_number")}>
              <TextInput
                name="organizationNumber"
                defaultValue={company.organizationNumber}
                maxLength={32}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t("common:address_line")}>
                <TextInput
                  name="addressLine"
                  defaultValue={company.primaryAddress?.addressLine || ""}
                  maxLength={200}
                />
              </Field>
            </div>
            <Field label={t("common:postcode")}>
              <TextInput
                name="postCode"
                defaultValue={company.primaryAddress?.postCode || ""}
                maxLength={20}
              />
            </Field>
            <Field label={t("common:city")}>
              <TextInput
                name="city"
                defaultValue={company.primaryAddress?.city || ""}
                maxLength={100}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label={t("common:country")}>
                <SelectCountry
                  id="countryCode"
                  defaultValue={company.primaryAddress?.countryCode || "NO"}
                />
              </Field>
            </div>
          </div>
          <div className="mt-6 border-t border-border pt-5">
            <h3 className="font-semibold text-text">
              {t("company:page.contact.title")}
            </h3>
            <p className="mt-1 text-sm leading-6 text-muted">
              {t("company:page.contact.description")}
            </p>
            <div className="mt-4 max-w-xl">
              <Field label={t("common:contact_email")}>
                <TextInput
                  name="contactEmail"
                  type="email"
                  defaultValue={company.contactEmail || ""}
                  maxLength={254}
                />
              </Field>
            </div>
          </div>
        </div>
        <aside className={`${cardClass} self-start bg-surface-muted/35`}>
          <h2 className="font-semibold text-text">
            {t("company:page.logo.title")}
          </h2>
          <p className="mt-1 text-sm leading-6 text-muted">
            {t("company:page.logo.description")}
          </p>
          <div className="mt-4">
            <CompanyLogoEditor
              company={company}
              isSaving={isSaving}
              onSave={onSave}
              onBusyChange={setLogoBusy}
            />
          </div>
        </aside>
      </div>
      <Button type="submit" variant="primary" disabled={isSaving || logoBusy}>
        {isSaving || logoBusy
          ? t("company:page.saving")
          : t("company:page.save")}
      </Button>
    </form>
  );
}

function PaymentForm({ company, isSaving, onSave }: FormProps) {
  const { t } = useTranslation(["common", "company"]);
  const [vat, setVat] = useState(
    company.paymentTerms?.isVatRegistered === true,
  );
  const [forwardInvoice, setForwardInvoice] = useState(
    company.accountingSettings?.forwardInvoice === true,
  );
  return (
    <form
      className="space-y-6 p-4 sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        void onSave({
          invoiceEmail: String(data.get("invoiceEmail") || "").trim() || null,
          paymentTerms: {
            accountNumber: String(data.get("accountNumber") || "").trim(),
            iban: String(data.get("iban") || "").trim(),
            swift: String(data.get("swift") || "").trim(),
            paymentDays: Number(data.get("paymentDays") || 0),
            isVatRegistered: vat,
          },
          accountingSettings: {
            accountingEmail: String(data.get("accountingEmail") || "").trim(),
            forwardInvoice,
          },
        }).catch(() => undefined);
      }}
    >
      <div className={cardClass}>
        <SectionHeading
          title={t("company:page.payment.title")}
          description={t("company:page.payment.description")}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("common:account_number")}>
            <TextInput
              name="accountNumber"
              defaultValue={company.paymentTerms?.accountNumber || ""}
              maxLength={64}
            />
          </Field>
          <Field label={t("common:payment_days")}>
            <TextInput
              name="paymentDays"
              type="number"
              min={0}
              max={365}
              defaultValue={company.paymentTerms?.paymentDays ?? 30}
            />
          </Field>
          <Field label={t("common:iban")}>
            <TextInput
              name="iban"
              defaultValue={company.paymentTerms?.iban || ""}
              maxLength={64}
            />
          </Field>
          <Field label={t("common:swift")}>
            <TextInput
              name="swift"
              defaultValue={company.paymentTerms?.swift || ""}
              maxLength={32}
            />
          </Field>
        </div>
        <div className="mt-5 flex items-center justify-between rounded-lg border border-border bg-surface-muted/35 p-4">
          <div id="company-vat">
            <p className="font-medium text-text">
              {t("common:is_vat_registered")}
            </p>
            <p className="mt-1 text-sm text-muted">
              {vat
                ? t("common:vat_registered_description")
                : t("common:not_vat_registered_description")}
            </p>
          </div>
          <ToggleSwitch
            checked={vat}
            onChange={() => setVat((value) => !value)}
            activeColorClass="bg-lgb-primary"
            ariaLabelledBy="company-vat"
          />
        </div>
        <div className="mt-6 border-t border-border pt-6">
          <h3 className="font-semibold text-text">
            {t("company:page.invoicing.title")}
          </h3>
          <p className="mt-1 text-sm leading-6 text-muted">
            {t("company:page.invoicing.description")}
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label={t("common:invoice_email")}>
              <TextInput
                name="invoiceEmail"
                type="email"
                defaultValue={company.invoiceEmail || ""}
                maxLength={254}
              />
            </Field>
            <Field label={t("common:accounting_email")}>
              <TextInput
                name="accountingEmail"
                type="email"
                defaultValue={company.accountingSettings?.accountingEmail || ""}
                maxLength={254}
              />
            </Field>
          </div>
          <div className="mt-5 flex items-center justify-between rounded-lg border border-border bg-surface-muted/35 p-4">
            <div id="company-forward-invoice">
              <p className="font-medium text-text">
                {t("common:forward_invoice")}
              </p>
              <p className="mt-1 text-sm text-muted">
                {forwardInvoice
                  ? t("common:forward_invoice_enabled_description")
                  : t("common:forward_invoice_disabled_description")}
              </p>
            </div>
            <ToggleSwitch
              checked={forwardInvoice}
              onChange={() => setForwardInvoice((value) => !value)}
              activeColorClass="bg-lgb-primary"
              ariaLabelledBy="company-forward-invoice"
            />
          </div>
        </div>
      </div>
      <Button type="submit" variant="primary" disabled={isSaving}>
        {isSaving ? t("company:page.saving") : t("company:page.save")}
      </Button>
    </form>
  );
}

function NotificationForm({ company, isSaving, onSave }: FormProps) {
  const { t } = useTranslation("company");
  const canManage = useCanManageWorkspace(company.workspaceId);
  const [confirmation, setConfirmation] = useState(
    company.notificationSettings?.sendOrderConfirmation === true,
  );
  const [tracking, setTracking] = useState(
    company.notificationSettings?.sendTrackingLink === true,
  );
  return (
    <form
      className="space-y-6 p-4 sm:p-6"
      onSubmit={(event) => {
        event.preventDefault();
        if (isSaving || !canManage) return;
        void onSave({
          notificationSettings: {
            sendOrderConfirmation: confirmation,
            sendTrackingLink: tracking,
          },
        }).catch(() => undefined);
      }}
    >
      <div className={cardClass}>
        <SectionHeading
          title={t("page.notifications.title")}
          description={t("page.notifications.description")}
        />
        <fieldset
          disabled={isSaving || !canManage}
          className="space-y-4 disabled:opacity-60"
        >
          {[
            {
              id: "confirmation",
              checked: confirmation,
              change: setConfirmation,
              description: t("page.notifications.confirmation.description"),
            },
            {
              id: "tracking",
              checked: tracking,
              change: setTracking,
              description: t("page.notifications.tracking.description"),
            },
          ].map(({ id, checked, change, description }) => (
            <div
              key={id}
              className="flex items-center justify-between gap-6 rounded-lg border border-border bg-surface-muted/35 p-4"
            >
              <div>
                <p id={`notification-${id}`} className="font-medium text-text">
                  {t(`page.notifications.${id}.title`)}
                </p>
                {description && (
                  <p className="mt-1 text-sm leading-6 text-muted">
                    {description}
                  </p>
                )}
              </div>
              <ToggleSwitch
                checked={checked}
                onChange={() => {
                  if (!isSaving && canManage) change(!checked);
                }}
                activeColorClass="bg-lgb-primary"
                ariaLabelledBy={`notification-${id}`}
              />
            </div>
          ))}
        </fieldset>
      </div>
      {!canManage && (
        <p className="text-sm text-muted">
          {t("page.notifications.read_only")}
        </p>
      )}
      <Button type="submit" variant="primary" disabled={isSaving || !canManage}>
        {t(isSaving ? "page.saving" : "page.save")}
      </Button>
    </form>
  );
}

interface FormProps {
  company: Company;
  isSaving: boolean;
  onSave: (updates: Partial<ICompany>) => Promise<void>;
}
